'use client';
import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { createContext, Suspense, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { cohesion, formatMonarchEvaluations, isNullish, monarch, normalizeError, } from '@redventures/cohesion-utils';
export const CohesionContext = createContext(undefined);
export function CohesionProvider({ children, components = {}, persistSlots = false, }) {
    const hasInitialized = useRef(false);
    const hasError = useRef(false);
    // Cohesion
    const hasTagularContext = useRef(false);
    // Monarch
    const [isLoading, setIsLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [result, setResult] = useState(null);
    const [rules, setRules] = useState({});
    const [slots, setSlots] = useState({});
    useEffect(() => {
        if (hasInitialized.current) {
            return;
        }
        else {
            hasInitialized.current = true;
        }
        // Cohesion Not Found
        if (!window.cohesion) {
            hasError.current = true;
            console.error('[cohesion-utils-react] Cohesion not found');
            setErr({ message: 'Cohesion not found' });
            setResult(null);
            setIsLoading(false);
            return;
        }
        // Monarch
        cohesion('monarch:done', (monarchErr, monarchResult) => {
            let newRules = {};
            const newSlots = {};
            const normalizedErr = normalizeError(monarchErr);
            if (normalizedErr) {
                hasError.current = true;
                console.error('[cohesion-utils-react] Monarch Error', normalizedErr);
            }
            if (monarchResult) {
                newRules = formatMonarchEvaluations(monarchResult) || {};
            }
            if (newRules.trafficFlow?.returnType === 'experience') {
                const experienceValues = newRules.trafficFlow.value.experienceValues ||
                    [];
                for (const experienceValue of experienceValues) {
                    newSlots[experienceValue.slot.customId] = experienceValue;
                }
            }
            setErr(normalizedErr);
            setResult(monarchResult);
            setIsLoading(false);
            setRules((oldRules) => ({ ...oldRules, ...newRules }));
            if (persistSlots === true) {
                setSlots((oldSlots) => ({ ...oldSlots, ...newSlots }));
            }
            else {
                setSlots(newSlots);
            }
        });
        // Cohesion
        cohesion('error', () => {
            hasError.current = true;
            console.error('[cohesion-utils-react] Cohesion failed to load');
            // Monarch
            setErr({ message: 'Cohesion failed to load' });
            setResult(null);
            setIsLoading(false);
        });
        cohesion('tagular:contextCreated', ({ autoDecision }) => {
            if (!hasTagularContext.current) {
                // Wait for Tagular context to be created during initial page load.
                hasTagularContext.current = true;
            }
            else if (autoDecision) {
                // Reset `isLoading` on soft navigations after the initial page load.
                // IMPORTANT: Only reset when _not_ in an errored state! Otherwise,
                // you'll get stuck loading until the next hard navigation.
                if (!hasError.current) {
                    setIsLoading(true);
                }
            }
        });
    }, []);
    const contextValue = {
        // Monarch
        err,
        result,
        isLoading,
        rules,
        slots,
        components,
    };
    return (_jsx(CohesionContext.Provider, { value: contextValue, children: children }));
}
// ------------------------------------------------------------
// HOOKS
// ------------------------------------------------------------
/**
 * Get Monarch evaluations.
 */
export function useMonarch() {
    const context = useContext(CohesionContext);
    if (!context) {
        throw new Error('useMonarch cannot be used outside of CohesionProvider');
    }
    return {
        err: context.err,
        result: context.result,
        isLoading: context.isLoading,
        rules: context.rules,
        slots: context.slots,
    };
}
/**
 * Get value for a Monarch Rule.
 */
export function useMonarchRule(ruleCustomId, options = {}) {
    const context = useContext(CohesionContext);
    if (!context) {
        throw new Error('useMonarchRule cannot be used outside of CohesionProvider');
    }
    const rule = context?.rules[ruleCustomId] ?? null;
    const value = useMemo(() => {
        const isReturnTypeAllowed = !options.returnTypes ||
            options.returnTypes.includes(rule?.returnType || '') ||
            options.returnTypes.includes(rule?.smartReturnType || '');
        if (!isNullish(rule?.value) && isReturnTypeAllowed) {
            return rule.value;
        }
        if (options.default instanceof Function) {
            return (options.default({
                err: context.err,
                isLoading: context.isLoading,
            }) ?? null);
        }
        return options.default ?? null;
    }, [rule, options, context.err, context.isLoading]);
    return {
        err: context.err,
        isLoading: context.isLoading,
        rule,
        value,
    };
}
/**
 * Send a new request for a Monarch Rule or Rule Set.
 */
export function useMonarchRequest(requestType, customId, options = {}) {
    const context = useContext(CohesionContext);
    if (!context) {
        throw new Error('useMonarchRequest cannot be used outside of CohesionProvider');
    }
    const hasInitialized = useRef(false);
    const [err, setErr] = useState(null);
    const [result, setResult] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    useEffect(() => {
        if (!options.skip) {
            monarch(requestType, customId, options.payload ?? {}, options.params ?? {}, (newErr, newResult) => {
                setErr(normalizeError(newErr));
                setResult(newResult);
                setIsLoading(false);
            });
        }
    }, [options.skip]);
    useEffect(() => {
        if (hasInitialized.current) {
            return;
        }
        else {
            hasInitialized.current = true;
        }
        cohesion('error', () => {
            setErr({ message: 'Cohesion failed to load' });
            setResult(null);
            setIsLoading(false);
        });
    }, []);
    const value = useMemo(() => {
        if (requestType === 'rule' && !isNullish(result?.[customId])) {
            return result?.[customId];
        }
        if (requestType === 'ruleset' && Object.keys(result ?? {}).length > 0) {
            return result;
        }
        if (options.default instanceof Function) {
            return (options.default({
                err,
                isLoading,
            }) ?? null);
        }
        return options.default ?? null;
    }, [result, customId, options, err, isLoading]);
    return {
        err,
        result,
        isLoading,
        value,
    };
}
/**
 * Get data for a Monarch Slot.
 */
export function useMonarchSlot(slotCustomId, options = {}) {
    const context = useContext(CohesionContext);
    if (!context) {
        throw new Error('useMonarchSlot cannot be used outside of CohesionProvider');
    }
    const experienceValue = context.slots[slotCustomId] ?? null;
    const data = useMemo(() => {
        const variant = experienceValue?.value ?? null;
        const isComponentAllowed = !options.components ||
            options.components.includes(variant?.componentCustomId);
        if (variant?.data && isComponentAllowed) {
            return variant.data;
        }
        if (options.default instanceof Function) {
            return (options.default({
                err: context.err,
                isLoading: context.isLoading,
            }) ?? null);
        }
        return options.default ?? null;
    }, [experienceValue, options, context.err, context.isLoading]);
    return {
        err: context.err,
        isLoading: context.isLoading,
        result: experienceValue,
        data,
    };
}
/**
 * Get data from a <MonarchSlot> context.
 */
export function useMonarchSlotContext(options = {}) {
    const context = useContext(MonarchSlotContext);
    if (!context) {
        throw new Error('useMonarchSlotContext cannot be used outside of a MonarchSlot');
    }
    const data = useMemo(() => {
        if (context.result?.value?.data) {
            return context.result.value?.data;
        }
        if (options.default instanceof Function) {
            return (options.default({
                err: context.err,
                isLoading: context.isLoading,
            }) ?? null);
        }
        return options.default ?? null;
    }, [context.result, options, context.err, context.isLoading]);
    return {
        err: context.err,
        isLoading: context.isLoading,
        result: context.result,
        data,
    };
}
export const MonarchSlotContext = createContext(undefined);
export function MonarchSlot({ children = _jsx(_Fragment, {}), slotId, components, fallback = _jsx(_Fragment, {}), }) {
    const context = useContext(CohesionContext);
    if (!context) {
        throw new Error('MonarchSlot cannot be used outside of CohesionProvider');
    }
    const { err, isLoading, result, data } = useMonarchSlot(slotId);
    const contextValue = useMemo(() => {
        return { err, isLoading, result, data };
    }, [slotId, err, isLoading, result, data]);
    if (isLoading) {
        return (_jsx(MonarchSlotContext.Provider, { value: contextValue, children: fallback }));
    }
    const componentCustomId = result?.value?.componentCustomId ?? '';
    const Component = components
        ? components[componentCustomId]
        : context.components[componentCustomId];
    return (_jsx(MonarchSlotContext.Provider, { value: contextValue, children: _jsx(Suspense, { fallback: fallback, children: Component ? _jsx(Component, {}) : children }) }));
}
